import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Plus, Pencil, Trash2, ArrowLeft } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { SkeletonTable } from '../components/ui/Skeleton';
import { ErrorState } from '../components/ui/EmptyState';
import AddInvoiceTemplateModal from '../components/invoices/AddInvoiceTemplateModal';
import EditInvoiceTemplateModal from '../components/invoices/EditInvoiceTemplateModal';
import DeleteInvoiceTemplateModal from '../components/invoices/DeleteInvoiceTemplateModal';
import { useInvoiceTemplatesByService } from '../hooks/useInvoiceTemplates';
import { useAllServices } from '../hooks/useServices';
import { formatCurrency } from '../utils/dateUtils';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';
import { useCurrency } from '../context/CurrencyContext';

const ServiceInvoiceTemplates = () => {
    const { uid } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { currency } = useCurrency();
    const { data: templates = [], isLoading: loading, error, refetch } = useInvoiceTemplatesByService(uid);
    const { data: allServices = [] } = useAllServices();
    const service = allServices.find((s) => s.uid === uid);

    const typeLabels = {
        electricity: t(KEYS.TYPE_ELECTRICITY),
        natural_gas: t(KEYS.TYPE_NATURAL_GAS),
        water: t(KEYS.TYPE_WATER),
        phone: t(KEYS.TYPE_PHONE),
        internet: t(KEYS.TYPE_INTERNET),
        other: t(KEYS.TYPE_OTHER),
    };

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const handleEdit = (template) => { setSelectedTemplate(template); setIsEditModalOpen(true); };
    const handleDelete = (template) => { setSelectedTemplate(template); setIsDeleteModalOpen(true); };

    const serviceTitle = service ? (service.name || typeLabels[service.type] || service.type) : 'Service';

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(`/services/${uid}`)}
                        className="p-1.5 rounded-md text-text-muted hover:text-accent hover:bg-surface-overlay transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-semibold text-text-primary">{t(KEYS.SERVICE_INVOICE_TEMPLATES_TITLE)}</h1>
                        <p className="text-sm text-text-muted mt-0.5">{serviceTitle}</p>
                    </div>
                </div>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => setIsAddModalOpen(true)}
                    className="gap-2"
                >
                    <Plus className="w-3.5 h-3.5" />
                    {t(KEYS.SERVICE_INVOICE_TEMPLATES_ADD)}
                </Button>
            </div>

            {/* Content */}
            {loading ? (
                <SkeletonTable rows={5} columns={5} />
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.SERVICE_INVOICE_TEMPLATES_ERROR_LOAD)} onRetry={refetch} />
            ) : templates.length > 0 ? (
                <Card padding="none" className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-surface-border">
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">{t(KEYS.SERVICE_INVOICE_TEMPLATES_COL_LABEL)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden sm:table-cell">{t(KEYS.SERVICE_INVOICE_TEMPLATES_COL_NAME)}</th>
                                    <th className="px-4 py-2.5 text-right text-[10px] font-medium text-text-muted uppercase tracking-widest hidden sm:table-cell">{t(KEYS.SERVICE_INVOICE_TEMPLATES_COL_AMOUNT)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden md:table-cell">{t(KEYS.SERVICE_INVOICE_TEMPLATES_COL_ISSUE_DATE)}</th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden md:table-cell">{t(KEYS.SERVICE_INVOICE_TEMPLATES_COL_DUE_DATE)}</th>
                                    <th className="px-4 py-2.5 w-20"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-border">
                                {templates.map((template, index) => (
                                    <tr
                                        key={template.uid}
                                        className="hover:bg-surface-overlay transition-colors group animate-appear"
                                        style={{ animationDelay: `${index * 30}ms` }}
                                    >
                                        <td className="px-4 py-2.5 text-sm font-medium text-text-primary">{template.label}</td>
                                        <td className="px-4 py-2.5 text-sm text-text-primary hidden sm:table-cell">{template.name || '—'}</td>
                                        <td className="px-4 py-2.5 text-sm font-mono text-right text-text-primary hidden sm:table-cell">
                                            {template.amount != null ? formatCurrency(template.amount, currency) : '—'}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-text-muted hidden md:table-cell">
                                            {template.issue_date || '—'}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-text-muted hidden md:table-cell">
                                            {template.due_date || '—'}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    onClick={() => handleEdit(template)}
                                                    className="p-2.5 sm:p-1.5 text-text-muted hover:text-accent rounded transition-colors"
                                                    title="Edit"
                                                >
                                                    <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(template)}
                                                    className="p-2.5 sm:p-1.5 text-text-muted hover:text-status-overdue rounded transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                    <p className="text-sm text-text-secondary mb-4">{t(KEYS.SERVICE_INVOICE_TEMPLATES_EMPTY)}</p>
                    <Button onClick={() => setIsAddModalOpen(true)} variant="primary" size="small">
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        {t(KEYS.SERVICE_INVOICE_TEMPLATES_ADD)}
                    </Button>
                </div>
            )}

            {/* Modals */}
            <AddInvoiceTemplateModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                serviceUid={uid}
            />
            <EditInvoiceTemplateModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                template={selectedTemplate}
            />
            <DeleteInvoiceTemplateModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                template={selectedTemplate}
            />
        </div>
    );
};

export default ServiceInvoiceTemplates;
